import React, { BaseSyntheticEvent, useEffect, useMemo, useState } from 'react';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { CheckboxGroup } from '@uhg-abyss/web/ui/CheckboxGroup';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
const SelectInputMulti = SelectInput;
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { BulkReferralResult, FindhelpProgram } from '@src/api.types';
import { useAtomValue } from 'jotai';
import { loggedInAdvocateAtom, memberInContextAtom } from '@src/global-state';
import { ReferralFormConsentText } from './ReferralForm/ConsentText';
import {API} from '@src/api';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import {
  getBulkReferral,
  removeFromBulkReferral
} from '@src/utills/storage';
import {
  filterOutPrimarySelected,
  formDataToFindhelpReferralRequest,
  findhelpServiceTagsIntoNeedSelectOptions,
} from './ReferralForm/helpers';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Box } from '@uhg-abyss/web/ui/Box';
import { FindhelpReferralRequest } from '@src/api.types';


export type BulkReferModalProps = {
  vendor: 'findhelp';
  referring: FindhelpProgram[];
  selectedPrograms: FindhelpProgram[];
  onClose: () => void;
  setReferResponse: (value: any) => void;
};

export type BulkReferModalData = {
  advocateFirstName: string;
  advocateLastName: string;
  advocateEmail: string;
  memberFirstName: string;
  memberLastName: string;
  memberEmail: string;
  memberPhone: string;
  contactPreferences: string[];
  selectedNeeds: {
    primaryNeeds: string[];
    secondaryNeeds: string[];
  };
  comment: string; // Not used
  consent: boolean;
};

export type SelectOption = {
  label: string;
  value: string;
};

export const BulkReferModal = ({
  vendor,
  referring,
  selectedPrograms,
  onClose,
  setReferResponse,
}: BulkReferModalProps) => {
  const loggedInAdvocate = useAtomValue(loggedInAdvocateAtom);
  const memberInContext = useAtomValue(memberInContextAtom);
  const [finalPrograms,setFinalPrograms]=useState<any[]>(selectedPrograms);

  const [isLoading, setIsLoading] = React.useState(false);

  const referralForm = useForm<BulkReferModalData>({
    defaultValues: {
      advocateFirstName: loggedInAdvocate?.first_name || '',
      advocateLastName: loggedInAdvocate?.last_name || '',
      advocateEmail: loggedInAdvocate?.email || '',
      memberId: memberInContext?.member_id || '',
      memberEmail: memberInContext?.email || '',
      memberFirstName: memberInContext?.first_name || '',
      memberLastName:
        memberInContext?.last_name
          .split(/(?<=\s|-)/)
          .map((word) => {
            return word.charAt(0).toUpperCase() + word.slice(1);
          })
          .join('') || '',
      memberPhone: memberInContext?.phone || '',
      contactPreferences: [],
      selectedNeeds: {
        primaryNeeds: [],
        secondaryNeeds: [],
      },
      comment: '',
      consent: false,
    },
  });

  const handleAcknowledgementValidation = () => {
    referralForm.trigger('consent');
  };
  const allPrograms = finalPrograms as FindhelpProgram[];
  const finalAcceptsReferrals = allPrograms.some(
    (program) => program.accepts_referrals
  );

  const loadProgramsUi = useMemo(() => {

    return finalPrograms.map((program, index) => {
      const needSelectOptions: SelectOption[] = findhelpServiceTagsIntoNeedSelectOptions(program.service_tags);

      return (
        <ReferralFormProgramInfoSection
          key={program.id}
          index={index}
          referralForm={referralForm}
          program={program}
          selectedPrograms={selectedPrograms}
          setFinalPrograms={setFinalPrograms}
          referring={referring}
          vendor={vendor}
          needSelectOptions={needSelectOptions}
          filterOutPrimarySelected={filterOutPrimarySelected}
        />
      );
    });
  }, [finalPrograms]);

  async function onSubmit(data: BulkReferModalData): Promise<any> {

    let req = finalPrograms.map((referringto,index)=>{
       data.selectedNeeds.primaryNeeds=data.selectedNeeds[referringto.id].primaryNeeds;
       data.selectedNeeds.secondaryNeeds=data.selectedNeeds[referringto.id].secondaryNeeds;
      return formDataToFindhelpReferralRequest(
      data,
      referringto as FindhelpProgram,
      memberInContext!,
      loggedInAdvocate!
    );
    });
    setIsLoading(true);
    const resp: BulkReferralResult | null = await API.submitBulkReferralFindhelp(
      req as FindhelpReferralRequest[]
    ) || null;
    if (resp === null) {
      console.warn('Referral response was null, this could indicate an error.');
      setReferResponse(null);
    } else if (resp === undefined) {
      console.error(
        'Referral response was undefined, this definitely indicates an error.'
      );
      setReferResponse(undefined);
    }
    else {
      // Backend may return a plain string on full success (e.g. "All requests processed successfully")
      // instead of a BulkReferralResult object. Normalize it so downstream components work correctly.
      const normalizedResp: BulkReferralResult = typeof resp === 'string'
        ? { totalRequests: 0, duplicatesRemoved: 0, alreadyExists: 0, processed: 0, successful: 0, failed: 0, failures: [] }
        : resp;
      setReferResponse(normalizedResp);
      if (normalizedResp.failures?.length === 0) {
        // Successful referral closes this modal
        onClose();
      }
    }
    // if (resp === null) {
    //   console.warn('Referral response was null, this could indicate an error.');
    //   setReferResponse(null);
    // } else if (resp === undefined) {
    //   console.error(
    //     'Referral response was undefined, this definitely indicates an error.'
    //   );
    //   setReferResponse(undefined);
    // } else {
    //   setReferResponse(resp);
    //   if (resp === 'All requests processed successfully') {
    //     // Successful referral closes this modal
    //     onClose();
    //   }
    // }
    setIsLoading(false);
  }

  return (
    <React.Fragment>
      <ModalDialog
        header="Bulk referrals"
        isOpen={true}
        onClose={onClose}
        size={1200}
        css={{
          'abyss-modal-content-container': {
            padding: '8px',
          },
        }}
      >
        <FormProvider state={referralForm} onSubmit={onSubmit}>
          <div style={{ padding: '0 16px' }}>
          {/* Advocate's information (form fields which are not editable) */}
          <ReferralFormAdvocateInfoSection
            referralForm={referralForm}
            vendor={vendor}
          />
          <PaddedLineBreak />

          {/* Member's info */}
          <ReferralFormMemberInfoSection
            referralForm={referralForm}
            vendor={vendor}
          />
          <PaddedLineBreak />

          {loadProgramsUi}

          {/* Member contact preferences */}
          <ReferralFormMemberContactPreferences referralForm={referralForm} />
          <PaddedLineBreak />

          <div>
            <Checkbox
              label={<span style={{ fontWeight: 'bold' }}>Confirm consent</span>}
              model="consent"
              onChange={handleAcknowledgementValidation}
              validators={{ required: 'Required' }}
            />

            <div
              style={{
                marginTop: '8px',
              }}
            >
              <ReferralFormConsentText
                vendor={vendor}
                acceptsReferrals={finalAcceptsReferrals}
              />
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '16px',
            }}
          >
            <Button isDisabled={!referralForm.formState.isValid || (finalPrograms==null||finalPrograms?.length==0)} type="submit">
              Refer
            </Button>
          </div>
          </div>
        </FormProvider>
        {isLoading ? (
          <div
            style={{
              zIndex: 999,
              backgroundColor: '#FFFFFF',
              opacity: 0.75,
              position: 'fixed',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              margin: 0,
              top: 0,
              left: 0,
            }}
          >
            <LoadingSpinner
              size="xl"
              isLoading={isLoading}
              ariaLoadingLabel="Referring...."
            />
          </div>
        ) : (
          ''
        )}
      </ModalDialog>
    </React.Fragment>
  );
};

const ReferralFormProgramInfoSection = ({
  index,
  referralForm,
  referring,
  program,
  vendor,
  needSelectOptions,
  selectedPrograms,
  setFinalPrograms,
  filterOutPrimarySelected,
}) => {
  const [secondaryNeeds, setSecondaryNeeds] = useState<any>([]);
  const handlePrimaryNeedChange = (index, selectedOption) => {
    referralForm.setValue(
      `selectedNeeds[${program.id}].primaryNeeds`,
      selectedOption
    );
    let sOption= filterOutPrimarySelected(
      selectedOption,
      needSelectOptions
    )
    setSecondaryNeeds(sOption);
    referralForm.trigger(`selectedNeeds[${program.id}].primaryNeeds`);
  };

  const handleSecondaryNeedChange = (index, selectedOptions) => {
    referralForm.setValue(
      `selectedNeeds[${program.id}].secondaryNeeds`,
      selectedOptions
    );
  };
  const handleRemove = () => {

    removeFromBulkReferral(program.id);
    setFinalPrograms([...getBulkReferral()]);
  };

  return (
    <FormProvider state={referralForm}>
      <div>
        <Card
          style={{
            marginBottom: '10px',
          }}
        >
          <Box color="white">
            <div>
              <Flex direction="row" justify="space-between" alignItems="flex-start">
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginRight: '16px',
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <Badge
                    outline
                    variant='info'
                    css={{
                      alignSelf: 'flex-start',
                      marginBottom: '8px',
                    }}
                    icon="save"
                    >
                    {program.accepts_referrals ? 'Refer' : 'Log Referral'}
                  </Badge>
                  <Text css={{ fontWeight: 'bold' }}>{program.name}</Text>

                  <Text
                    data-cy="referral-form-program-provider-name"
                    css={{
                      fontSize: '0.875rem',
                      fontWeight: 'normal',
                      marginTop: '4px'
                    }}
                  >
                    <span>by </span>
                    <span>{program.provider_name}</span>
                  </Text>
                </div>
                <div style={{ flexShrink: 0 }}>
                  <Flex direction="row" justify="space-between">
                    <div
                      style={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'flex-start',
                      }}
                    >
                      <SelectInput
                        label={<span style={{ fontWeight: 'bold' }}>Primary Need</span>}
                        placeholder="Select primary need"
                        width="250px"
                        options={needSelectOptions}
                        onChange={(selectedOption) =>
                          handlePrimaryNeedChange(index, selectedOption)
                        }
                        onBlur={() =>
                          referralForm.trigger(
                            `selectedNeeds[${program.id}].primaryNeeds`
                          )
                        }
                        validators={{ required: 'Required' }}
                        model={`selectedNeeds[${program.id}].primaryNeeds`}

                      />
                      <SelectInputMulti
                        label={<span style={{ fontWeight: 'bold' }}>Secondary Need</span>}
                        placeholder="Select secondary need"
                        width="250px"
                        options={secondaryNeeds}
                        isMulti
                        onChange={(selectedOptions) =>
                          handleSecondaryNeedChange(index, selectedOptions)
                        }
                        validators={{ required: false }}
                        model={`selectedNeeds[${program.id}].secondaryNeeds`}
                      />
                    </div>

                    <div
                      style={{
                        paddingTop: '30px',
                        paddingLeft: '16px',
                      }}
                    >
                      <Button onClick={handleRemove}>Remove</Button>
                    </div>
                  </Flex>
                </div>
              </Flex>
            </div>
          </Box>
        </Card>
      </div>
    </FormProvider>
  );
};

const ReferralFormAdvocateInfoSection = ({
  referralForm,
  vendor,
}: {
  referralForm: ReturnType<typeof useForm>;
  vendor: 'findhelp';
}) => {
  return (
    <div>
      <Layout.Group grow>
        <TextInput
          label={<span style={{ fontWeight: 'bold' }}>Your First Name</span>}
          model="advocateFirstName"
          validators={{ required: true }}
          readOnly
          isDisabled
          isRequired
        />

        <TextInput
          label={<span style={{ fontWeight: 'bold' }}>Your Last Name</span>}
          model="advocateLastName"
          validators={{ required: true }}
          readOnly
          isDisabled
          isRequired
        />
      </Layout.Group>

      <Layout.Group
        grow
        style={{
          paddingTop: '4px',
        }}
      >
        <TextInput
          label={<span style={{ fontWeight: 'bold' }}>E-mail</span>}
          model="advocateEmail"
          validators={{ required: true }}
          readOnly
          isDisabled
          isRequired
          data-cy="referral-form-advocate-email"
        />
      </Layout.Group>
    </div>
  );
};

const ReferralFormMemberInfoSection = ({
  referralForm,
  vendor,
}: {
  referralForm: ReturnType<typeof useForm>;
  vendor: 'findhelp';
}) => {
  const handleFirstNameValidation = () => {
    referralForm.trigger('memberFirstName');
  };
  const handleLastNameValidation = () => {
    referralForm.trigger('memberLastName');
  };
  const handleEmailAddressValidation = () => {
    referralForm.trigger('memberEmail');
  };
  const handlePhoneNumberValidation = () => {
    referralForm.trigger('memberPhone');
  };

  return (
    <FormProvider state={referralForm}>
      <div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
          <TextInput
            label={<span style={{ fontWeight: 'bold' }}>Member's First Name</span>}
            model="memberFirstName"
            onChange={handleFirstNameValidation}
            onBlur={handleFirstNameValidation}
            validators={{ required: true }}
            isRequired
            data-cy="referral-form-member-first-name"
          />

          <TextInput
            label={<span style={{ fontWeight: 'bold' }}>Member's Last Name</span>}
            model="memberLastName"
            onChange={handleLastNameValidation}
            onBlur={handleLastNameValidation}
            validators={{ required: true }}
            isRequired
            data-cy="referral-form-member-last-name"
          />
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '4px', alignItems: 'flex-start' }}>
          <TextInput
            label={<span style={{ fontWeight: 'bold' }}>Member's Phone</span>}
            model="memberPhone"
            onChange={handlePhoneNumberValidation}
            onBlur={handlePhoneNumberValidation}
            placeholder="(___) ___-____"
            mask="phone"
            // Becomes required if the user selects "phone" as a contact preference
            validators={
              referralForm.getValues('contactPreferences').includes('phone') ||
              referralForm.getValues('contactPreferences').includes('text')
                ? { required: 'Required' }
                : { required: false }
            }
          />

          <TextInput
            label={<span style={{ fontWeight: 'bold' }}>Member's Email</span>}
            model="memberEmail"
            onChange={handleEmailAddressValidation}
            onBlur={handleEmailAddressValidation}
            // Becomes required if the user selects "email" as a contact preference
            validators={
              referralForm.getValues('contactPreferences').includes('email')
                ? { required: 'Required' }
                : { required: false }
            }
          />
        </div>
      </div>
    </FormProvider>
  );
};

const ReferralFormMemberContactPreferences = ({
  referralForm,
}: {
  referralForm: ReturnType<typeof useForm>;
}) => {
  const handleContactPreferenceValidation = () => {
    referralForm.trigger('contactPreferences');
  };
  return (
    <FormProvider state={referralForm}>
      <div>
        <CheckboxGroup
          label={<span style={{ fontWeight: 'bold' }}>Best way to reach the member (This is how the member would like the CBO to contact them.)</span>}
          css={{
            'abyss-checkbox-group-label-container': {
              paddingLeft: '0',
            },
            'abyss-checkbox-group-container': {
              paddingLeft: '0',
            },
            'abyss-checkbox-root': {
              paddingRight: '16px',
            },
          }}
          model="contactPreferences"
          onChange={handleContactPreferenceValidation}
          validators={{
            required: 'Required',
          }}
        >
          <Checkbox
            value="phone"
            label="Phone call"
            css={{
              'abyss-checkbox-root': {
                paddingRight: '16px',
              },
            }}
            onChange={(_e: BaseSyntheticEvent) => {
              if (_e.target.checked) {
                if (
                  referralForm
                    .getValues('contactPreferences')
                    .includes('do not contact')
                ) {
                  const updated = referralForm.getValues('contactPreferences');
                  referralForm.setValue(
                    'contactPreferences',
                    updated.filter((pref: string) => pref !== 'do not contact')
                  );
                }
              } else {
                if (!referralForm.getValues('contactPreferences').includes('text')) {
                  referralForm.clearErrors('memberPhone');
                }
              }
            }}
          />
          <Checkbox
            value="text"
            label="Text message"
            css={{
              'abyss-checkbox-root': {
                paddingRight: '16px',
              },
            }}
            onChange={(_e: BaseSyntheticEvent) => {
              if (_e.target.checked) {
                if (
                  referralForm
                    .getValues('contactPreferences')
                    .includes('do not contact')
                ) {
                  const updated = referralForm.getValues('contactPreferences');
                  referralForm.setValue(
                    'contactPreferences',
                    updated.filter((pref: string) => pref !== 'do not contact')
                  );
                }
              } else {
                if (!referralForm.getValues('contactPreferences').includes('phone')) {
                  referralForm.clearErrors('memberPhone');
                }
              }
            }}
          />
          <Checkbox
            value="email"
            label="E-mail"
            css={{
              'abyss-checkbox-root': {
                paddingRight: '16px',
              },
            }}
            onChange={(_e: BaseSyntheticEvent) => {
              if (_e.target.checked) {
                if (
                  referralForm
                    .getValues('contactPreferences')
                    .includes('do not contact')
                ) {
                  const updated = referralForm.getValues('contactPreferences');
                  referralForm.setValue(
                    'contactPreferences',
                    updated.filter((pref: string) => pref !== 'do not contact')
                  );
                }
              } else {
                referralForm.clearErrors('memberEmail');
              }
            }}
          />
          <Checkbox
            label="Don't reach out"
            value="do not contact"
            onChange={(e: BaseSyntheticEvent) => {
              if (e.target.checked) {
                referralForm.setValue('contactPreferences', ['do not contact']);
                referralForm.clearErrors('memberPhone');
                referralForm.clearErrors('memberEmail');
              } else {
                referralForm.setValue('contactPreferences', []);
              }
            }}
          />
        </CheckboxGroup>
        <Text
          css={{
            marginTop: '8px',
            lineHeight: '1.8',
          }}
        >
          If you select "Don't reach out" or if the CBO is noted with "Log Referral" you must provide the "Next Steps" information to the member either verbally or by email so that the member can contact the CBO.
        </Text>
      </div>
    </FormProvider>
  );
};
const PaddedLineBreak = () => (
  <hr
    style={{
      marginTop: '16px',
      marginBottom: '8px',
    }}
  />
);